import { useState } from 'react';
import { Zap, Shield, Heart, Globe, Smartphone } from 'lucide-react';

interface GuestDashboardProps {
  onMemberLogin: () => void;
}

export default function GuestDashboard({ onMemberLogin }: GuestDashboardProps) {
  const [language, setLanguage] = useState<'ja' | 'en'>('ja');

  const content = {
    ja: {
      title: 'SNS Launcher',
      subtitle: 'お気に入りのSNSアプリへ素早くアクセス',
      description: 'SNS Launcherは、日本で最も利用されているソーシャルメディアプラットフォーム（YouTube、Instagram、Facebook、X）への便利なショートカットを提供するシンプルなユーティリティアプリです。',
      memberLogin: '研究参加者の方はこちら',
      memberLoginButton: 'メンバーログイン',
      features: [
        {
          icon: Zap,
          title: 'ワンタップでアクセス',
          description: 'お気に入りのSNSをすぐに開けます。ブラウザを開いてURLを入力する手間が省けます。'
        },
        {
          icon: Smartphone,
          title: 'カスタマイズ可能',
          description: '独自のショートカットを追加したり、色やアイコンを変更したり、並び順を自由にカスタマイズできます。'
        },
        {
          icon: Shield,
          title: 'プライバシー重視',
          description: 'データ収集は一切行いません。すべての設定はデバイスに保存され、外部に送信されません。'
        },
        {
          icon: Heart,
          title: 'シンプルで使いやすい',
          description: '直感的なインターフェースで、誰でも簡単に使えます。複雑な設定は必要ありません。'
        }
      ],
      platforms: {
        title: '対応プラットフォーム',
        subtitle: '日本で人気のSNSをサポート',
        items: [
          {
            name: 'YouTube',
            color: '#FF0000',
            svgPath: 'M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22 1.1.28 1.9.07.8.1 1.49.1 2.09L22 12c0 2.19-.16 3.8-.44 4.83-.25.9-.83 1.48-1.73 1.73-.47.13-1.33.22-2.65.28-1.3.07-2.49.1-3.59.1L12 19c-4.19 0-6.8-.16-7.83-.44-.9-.25-1.48-.83-1.73-1.73-.13-.47-.22-1.1-.28-1.9-.07-.8-.1-1.49-.1-2.09L2 12c0-2.19.16-3.8.44-4.83.25-.9.83-1.48 1.73-1.73.47-.13 1.33-.22 2.65-.28 1.3-.07 2.49-.1 3.59-.1L12 5c4.19 0 6.8.16 7.83.44.9.25 1.48.83 1.73 1.73z'
          },
          {
            name: 'Instagram',
            color: '#E4405F',
            svgPath: 'M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z'
          },
          {
            name: 'Facebook',
            color: '#1877F2',
            svgPath: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z'
          },
          {
            name: 'X',
            color: '#000000',
            svgPath: 'M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z'
          }
        ]
      },
      cta: {
        title: '今すぐ始めましょう',
        description: 'App StoreまたはGoogle Playから無料でダウンロードできます。',
        button: 'ダウンロード'
      }
    },
    en: {
      title: 'SNS Launcher',
      subtitle: 'Quick Access to Your Favorite Social Media',
      description: 'SNS Launcher is a simple utility app that provides convenient shortcuts to the most popular social media platforms in Japan (YouTube, Instagram, Facebook, X).',
      memberLogin: 'For research participants',
      memberLoginButton: 'Member Login',
      features: [
        {
          icon: Zap,
          title: 'One-Tap Access',
          description: 'Open your favorite social media instantly. Save time from opening browsers and typing URLs.'
        },
        {
          icon: Smartphone,
          title: 'Fully Customizable',
          description: 'Add your own shortcuts, change colors and icons, and reorder apps to match your preferences.'
        },
        {
          icon: Shield,
          title: 'Privacy First',
          description: 'No data collection. All settings are stored locally on your device and never transmitted externally.'
        },
        {
          icon: Heart,
          title: 'Simple & Easy',
          description: 'Intuitive interface that anyone can use. No complicated setup required.'
        }
      ],
      platforms: {
        title: 'Supported Platforms',
        subtitle: 'Popular social media in Japan',
        items: [
          {
            name: 'YouTube',
            color: '#FF0000',
            svgPath: 'M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22 1.1.28 1.9.07.8.1 1.49.1 2.09L22 12c0 2.19-.16 3.8-.44 4.83-.25.9-.83 1.48-1.73 1.73-.47.13-1.33.22-2.65.28-1.3.07-2.49.1-3.59.1L12 19c-4.19 0-6.8-.16-7.83-.44-.9-.25-1.48-.83-1.73-1.73-.13-.47-.22-1.1-.28-1.9-.07-.8-.1-1.49-.1-2.09L2 12c0-2.19.16-3.8.44-4.83.25-.9.83-1.48 1.73-1.73.47-.13 1.33-.22 2.65-.28 1.3-.07 2.49-.1 3.59-.1L12 5c4.19 0 6.8.16 7.83.44.9.25 1.48.83 1.73 1.73z'
          },
          {
            name: 'Instagram',
            color: '#E4405F',
            svgPath: 'M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z'
          },
          {
            name: 'Facebook',
            color: '#1877F2',
            svgPath: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z'
          },
          {
            name: 'X',
            color: '#000000',
            svgPath: 'M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z'
          }
        ]
      },
      cta: {
        title: 'Get Started Now',
        description: 'Free download from App Store or Google Play.',
        button: 'Download'
      }
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header with Language Toggle */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/app_icon.png"
              alt="SNS Launcher"
              className="w-10 h-10 rounded-full shadow-sm"
            />
            <h1 className="text-xl font-bold text-gray-800">SNS Launcher</h1>
          </div>

          {/* Language Toggle */}
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${language === 'ja' ? 'text-blue-600' : 'text-gray-500'}`}>
              日本語
            </span>
            <button
              onClick={() => setLanguage(language === 'ja' ? 'en' : 'ja')}
              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              style={{ backgroundColor: language === 'en' ? '#3b82f6' : '#9ca3af' }}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  language === 'en' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${language === 'en' ? 'text-blue-600' : 'text-gray-500'}`}>
              English
            </span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center mb-6">
            <img
              src="/app_icon.png"
              alt="SNS Launcher"
              className="w-24 h-24 rounded-3xl shadow-xl"
            />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600 mb-2">{t.subtitle}</p>
          <p className="max-w-2xl mx-auto text-gray-600 leading-relaxed">
            {t.description}
          </p>
        </div>

        {/* App Preview with Japan SNS Usage Statistics - Combined */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 mb-16">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-gray-800 mb-2">
              {language === 'ja' ? '日本のSNS利用状況（2025年）' : 'Social Media Usage in Japan (2025)'}
            </h3>
            <p className="text-gray-600">
              {language === 'ja' ? 'アプリで対応するプラットフォーム' : 'Platforms Supported in App'}
            </p>
          </div>

          {/* iPhone + Bar Chart Combined */}
          <div className="flex flex-col lg:flex-row items-center justify-center gap-16">
            {/* iPhone Mockup */}
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="relative">
                {/* iPhone 15 Pro Frame */}
                <div className="relative bg-gray-900 rounded-[3rem] p-3 shadow-2xl" style={{ width: '280px' }}>
                  {/* Dynamic Island */}
                  <div className="absolute top-5 left-1/2 transform -translate-x-1/2 w-24 h-7 bg-black rounded-full z-10"></div>
                  {/* Screen */}
                  <div className="relative bg-white rounded-[2.5rem] overflow-hidden" style={{ aspectRatio: '9/19.5' }}>
                    <img
                      src="/screenshots/ios_home.png"
                      alt="iOS App Preview"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                {/* Home indicator */}
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-white/30 rounded-full"></div>
              </div>
            </div>

            {/* Vertical Bar Chart */}
            <div className="flex-1 max-w-xl">
              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mb-8 flex-wrap">
                {[
                  { name: 'YouTube', color: '#FF0000' },
                  { name: 'Instagram', color: '#E4405F' },
                  { name: 'X', color: '#000000' },
                  { name: 'Facebook', color: '#1877F2' },
                ].map((platform, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: platform.color }}></div>
                    <span className="text-sm font-medium text-gray-700">{platform.name}</span>
                  </div>
                ))}
              </div>

              {/* Bar Chart */}
              <div className="flex items-end justify-around h-80 gap-6 px-4">
                {[
                  { name: 'YouTube', users: 78.7, color: '#FF0000' },
                  { name: 'Instagram', users: 57.5, color: '#E4405F' },
                  { name: 'X', users: 66.5, color: '#000000' },
                  { name: 'Facebook', users: 16.2, color: '#1877F2' },
                ].map((platform, index) => (
                  <div key={index} className="flex flex-col items-center flex-1">
                    <div className="text-center mb-2">
                      <div className="text-lg font-bold text-gray-800">{platform.users}M</div>
                    </div>
                    <div
                      className="w-full rounded-t-xl transition-all duration-1000 ease-out relative group shadow-lg"
                      style={{
                        height: `${(platform.users / 78.7) * 280}px`,
                        backgroundColor: platform.color,
                      }}
                    >
                      {/* Hover tooltip */}
                      <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white px-3 py-1 rounded text-xs whitespace-nowrap">
                        {platform.users}M
                      </div>
                    </div>
                    <div className="mt-3 text-center">
                      <div className="font-semibold text-gray-800 text-sm">{platform.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-sm text-gray-500">
            {language === 'ja'
              ? '(多くの情報源から集められた調査であり、参考情報のみ)'
              : '(Aggregated from multiple sources, for reference only)'}
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {t.features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
                      <Icon className="w-7 h-7 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Supported Platforms */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 mb-16">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-gray-800 mb-2">
              {t.platforms.title}
            </h3>
            <p className="text-gray-600">{t.platforms.subtitle}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {t.platforms.items.map((platform, index) => (
              <div
                key={index}
                className="flex flex-col items-center gap-3 p-6 rounded-2xl hover:bg-gray-50 transition-colors"
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg p-3"
                  style={{ backgroundColor: platform.color }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="white"
                    className="w-full h-full"
                  >
                    <path d={platform.svgPath} />
                  </svg>
                </div>
                <span className="font-semibold text-gray-800">
                  {platform.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Member Login CTA */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl shadow-2xl p-8 md:p-12 text-center text-white">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
            <Globe className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            {t.memberLogin}
          </h3>
          <p className="text-blue-100 mb-8 max-w-md mx-auto">
            {language === 'ja'
              ? '研究プロジェクトに参加されている方は、メンバーコードでログインしてください。'
              : 'If you are participating in our research project, please log in with your member code.'
            }
          </p>
          <button
            onClick={onMemberLogin}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-gray-50 text-blue-600 rounded-xl font-bold text-lg transition-colors shadow-xl"
          >
            <Shield className="w-6 h-6" />
            {t.memberLoginButton}
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 text-sm text-gray-600">
          <p>© 2025 SNS Launcher. {language === 'ja' ? 'すべての権利を保有します。' : 'All rights reserved.'}</p>
        </div>
      </div>
    </div>
  );
}
